import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Collapse,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { styled } from "@mui/system";
import {
  FaTrophy,
  FaChevronDown,
  FaChevronUp,
  FaCrown,
  FaGamepad
} from "react-icons/fa";
import { Particles } from "../components/Particles.tsx";
import Navbar from "../components/Navbar.tsx";
import DashBoardNavBar from "../components/DashBoardNavBar.tsx";

// Frissített interfész az API adatainak megfelelően
interface Player {
  id: number;
  username: string;
  aimScore: number;
  avatarUrl: string;
}

const StyledPaper = styled(Paper)<{ rank: number }>(({ theme, rank }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(1, 0),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  transition: "all 0.3s ease",
  backgroundColor:
    rank === 1 ? "#ffd700" : rank === 2 ? "#c0c0c0" : rank === 3 ? "#cd7f32" : "#2d2d2d",
  border: `2px solid ${rank <= 3 ? "#ff4081" : "#333"}`,
  borderRadius: "15px",
  color: rank <= 3 ? "#000" : "#fff",
  "&:hover": {
    transform: "translateX(10px)",
    boxShadow: "0 0 20px rgba(255, 64, 129, 0.3)"
  }
}));

const RankCircle = styled(Box)<{ rank: number }>(({ rank }) => ({
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  color: "#fff",
  backgroundColor:
    rank === 1 ? "#ffd700" : rank === 2 ? "#c0c0c0" : rank === 3 ? "#cd7f32" : "#444",
  border: "3px solid #ff4081",
  boxShadow: "0 0 10px rgba(255, 64, 129, 0.5)"
}));

const PlayerCard: React.FC<{ player: Player; rank: number }> = ({ player, rank }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const avatarUrl =
    player.avatarUrl && player.avatarUrl !== ""
      ? player.avatarUrl
      : `https://api.dicebear.com/7.x/identicon/svg?seed=default`;

  return (
    <StyledPaper rank={rank} elevation={3}>
      <RankCircle rank={rank}>
        {rank === 1 ? <FaCrown size={24} /> : rank}
      </RankCircle>
      <Avatar
        src={avatarUrl}
        sx={{
          width: isMobile ? 40 : 50,
          height: isMobile ? 40 : 50,
          border: "2px solid #ff4081"
        }}
      />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"}>{player.username}</Typography>
        <Typography variant="body2" color={rank <= 3 ? "text.primary" : "#fff"}>
          Score: {player.aimScore.toLocaleString()}
        </Typography>
      </Box>
      {rank === 1 && <FaTrophy size={isMobile ? 20 : 24} color="#ffd700" />}
    </StyledPaper>
  );
};

const AIMLeaderBoard = () => {
  const [expanded, setExpanded] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rank, setRank] = useState<number | null>(null); // A bejelentkezett felhasználó rangja
  const theme = useTheme();
  const isAuthenticated = !!Cookies.get("token");

  useEffect(() => {
    // Lekérjük a top 10 játékost a megadott API végpontról
    fetch(`${process.env.REACT_APP_API_URL}/v1/user/public/gettoptenaimhighscores`)
      .then((response) => response.json())
      .then((data: Player[]) => {
        setPlayers(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load leaderboard data");
        setLoading(false);
      });

    // Ha a felhasználó be van jelentkezve, lekérjük a rangját a megfelelő API végpontról
    if (isAuthenticated) {
        fetch(`${process.env.REACT_APP_API_URL}/v1/user/aimscorerank`, {
          headers: {
            "Authorization": `Bearer ${Cookies.get("token")}`
          }
        })
          .then((response) => response.json())
          .then((data: { aimscorerank: number }) => {
            // Egyszerűen beállítjuk az értéket, majd a render részben ellenőrizzük -1 esetét
            setRank(data.aimscorerank);
          })
          .catch(() => {
            setError("Failed to load user rank");
          });
      }      
  }, [isAuthenticated]);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        mt: '64px',
        backgroundColor: theme.palette.background.default
      }}
    >
      <div style={{ zIndex: 10 }}>
        {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      </div>

      <div
        style={{
          position: "absolute",
          top: 80,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0
        }}
      >
        <Particles />
      </div>

      <Container
        maxWidth="md"
        sx={{
          py: 4,
          backgroundColor: theme.palette.background.paper,
          top: "20px",
          borderRadius: "30px",
          boxShadow: "0 0 50px rgba(255, 64, 128, 0.64)",
          position: "relative",
          zIndex: 2
        }}
      >
        <Box sx={{ mb: 4, textAlign: "center", position: "relative" }}>
            {/* Ikon pozicionálása a jobb felső sarokban */}
            <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                <ControlCameraIcon fontSize="large"/>
            </Box>
            <Typography variant="h3" component="h1" gutterBottom>
                <FaGamepad size={32} color="#ff4081" /> AIM Top Players
            </Typography>

          {isAuthenticated ? (
            rank !== null && (
                <Typography variant="h6" color="textSecondary">
                Your Rank: {rank === -1 ? "You don't have a score yet" : rank}
                </Typography>
            )
            ) : (
            <Typography variant="h6" color="textSecondary">
                Sign Up or Sign In and get your rank!🏆
            </Typography>
            )}


          {players.length > 3 && (
            <IconButton onClick={() => setExpanded(!expanded)} sx={{ color: "#ff4081" }}>
              {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </IconButton>
          )}
        </Box>

        {loading ? (
          <CircularProgress sx={{ color: "#ff4081" }} />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Stack spacing={2}>
            {players.slice(0, 3).map((player, index) => (
              <PlayerCard key={player.username} player={player} rank={index + 1} />
            ))}
            <Collapse in={expanded}>
              {players.slice(3).map((player, index) => (
                <PlayerCard key={player.username} player={player} rank={index + 4} />
              ))}
            </Collapse>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default AIMLeaderBoard;
